import { PUBLIC_ALLERGENS } from './publicAllergens';

/**
 * Italian display names for allergens (by code or name key).
 * Used on public menu pages to show allergen names in Italian.
 * 14 EU allergens (A–N) use names from publicAllergens; rest fall back to this map.
 */
const ALLERGEN_NAMES_IT: Record<string, string> = {
  gluten: 'Glutine',
  cereals_containing_gluten: 'Cereali contenenti glutine',
  crustaceans: 'Crostacei',
  eggs: 'Uova',
  fish: 'Pesce',
  peanuts: 'Arachidi',
  soybeans: 'Soia',
  soy: 'Soia',
  milk: 'Latte',
  nuts: 'Frutta a guscio',
  tree_nuts: 'Frutta a guscio',
  celery: 'Sedano',
  mustard: 'Senape',
  sesame: 'Semi di sesamo',
  sesame_seeds: 'Semi di sesamo',
  sulphur_dioxide: 'Anidride solforosa e solfiti',
  sulphites: 'Solfiti',
  lupin: 'Lupini',
  molluscs: 'Molluschi',
  shellfish: 'Crostacei e molluschi',
  lactose: 'Lattosio',
  glu: 'Glutine',
  egg: 'Uova',
  lac: 'Latte',
  nut: 'Frutta a guscio',
  she: 'Crostacei',
};

function toKey(s: string): string {
  return (s || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export interface AllergenForDisplay {
  id: number;
  name: string;
  code?: string;
  icon?: string;
  description?: string;
}

/**
 * Returns the Italian display name for an allergen when available,
 * otherwise the original name. Uses publicAllergens (14 EU) when allergen has code A–N.
 */
export function getAllergenDisplayNameItalian(allergen: AllergenForDisplay): string {
  if (!allergen) return '';
  const code = (allergen.code || '').toString().toUpperCase();
  const fromPublic = PUBLIC_ALLERGENS.find((a) => a.code === code);
  if (fromPublic) return fromPublic.nameIt;
  const nameKey = toKey(allergen.name);
  if (nameKey && ALLERGEN_NAMES_IT[nameKey]) return ALLERGEN_NAMES_IT[nameKey];
  return allergen.name || '';
}

/** Italian descriptions for the 14 EU allergens (by code A–N). */
const ALLERGEN_DESCRIPTIONS_IT: Record<string, string> = {
  A: 'Contiene glutine da grano, segale, orzo, avena',
  B: 'Crostacei come gamberi, granchi, aragoste',
  C: 'Uova e prodotti a base di uova',
  D: 'Pesce e prodotti a base di pesce',
  E: 'Arachidi e prodotti a base di arachidi',
  F: 'Soia e prodotti a base di soia',
  G: 'Latte e prodotti a base di latte (incluso lattosio)',
  H: 'Frutta a guscio (mandorle, nocciole, noci, ecc.)',
  I: 'Sedano e prodotti a base di sedano',
  J: 'Senape e prodotti a base di senape',
  K: 'Semi di sesamo e prodotti a base di sesamo',
  L: 'Anidride solforosa e solfiti',
  M: 'Lupini e prodotti a base di lupini',
  N: 'Molluschi e prodotti a base di molluschi',
};

/**
 * Returns the allergen description in Italian. For EU allergens (code A–N) uses the fixed Italian text;
 * otherwise returns the stored description (e.g. custom allergens).
 */
export function getAllergenDescriptionItalian(allergen: AllergenForDisplay): string {
  if (!allergen) return '';
  const code = (allergen.code || '').toString().toUpperCase();
  if (code && ALLERGEN_DESCRIPTIONS_IT[code]) return ALLERGEN_DESCRIPTIONS_IT[code];
  return allergen.description || '';
}
